import React, { forwardRef } from 'react';
import { Box, FormHelperText } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useEntityPermission } from '../../providers/EntityPermissionProvider/index';
import ReadOnly from './ReadOnly';
import NumberFieldBaseUI from './NumberFieldBaseUI';

const DecimalField = forwardRef(
  (
    {
      placeholder,
      disabled = false,
      min = 0,
      helperText,
      max = null,
      ...props
    },
    ref,
  ) => {
    const { control, getValues } = useFormContext();
    const { isHidden, isReadOnly } = useEntityPermission();

    const handleKeyDown = event => {
      const input = event.target.value;
      const selectionStart = event.target.selectionStart || 0;
      const decimalIndex = input.indexOf('.');
      const decimals = decimalIndex >= 0 ? input.slice(decimalIndex + 1) : '';

      // Prevent typing more than 2 decimals
      if (
        decimalIndex >= 0 &&
        selectionStart > decimalIndex &&
        decimals.length >= 2 &&
        !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(event.key)
      ) {
        event.preventDefault();
      }

      // Optional: prevent "-" key
      if (event.key === '-') {
        event.preventDefault();
      }
    };

    const currentvalue = getValues(props?.name);

    if (isReadOnly(props?.permission, currentvalue)) {
      return <ReadOnly name={props?.name}>{currentvalue}</ReadOnly>;
    }

    if (isHidden(props?.permission)) {
      return <input type="hidden" name={props?.name} value={currentvalue} />;
    }

    return (
      <Box ref={ref}>
        <Controller
          control={control}
          name={props?.name}
          render={({ field, fieldState: { error } }) => {
            return (
              <Box>
                <NumberFieldBaseUI
                  {...field}
                  value={field.value ?? null}
                  format={{
                    style: 'decimal',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                    useGrouping: false,
                  }}
                  step={0.01}
                  placeholder={placeholder}
                  disabled={disabled}
                  onKeyDown={handleKeyDown}
                  min={min}
                  error={error?.message}
                  {...(max && { max })}
                />
                {error || helperText ? (
                  <FormHelperText
                    sx={{ typography: 'body2' }}
                    error={Boolean(error)}>
                    {error?.message || helperText}
                  </FormHelperText>
                ) : null}
              </Box>
            );
          }}
        />
      </Box>
    );
  },
);

export default DecimalField;
